import { useCallback, useEffect, useState } from 'react';
import { useAction } from '@/lib/api-hooks';
import toast from 'react-hot-toast';
import { Plus, RefreshCw } from 'lucide-react';
import { backendApi } from '@/lib/backend';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Loader } from '@/components/ui/Loader';

type IvrMenuOption = {
  key: string;
  destType: string;
  destValue: string;
  label: string;
};

type Ivr = {
  id: number;
  number: string;
  name: string;
  prompt?: string;
  menu?: IvrMenuOption[];
  timeout?: IvrMenuOption;
  invalid?: IvrMenuOption;
};

interface IvrsTabProps {
  customerId: string;
}

function KeyChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-md bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200 text-xs font-bold font-mono">
      {label}
    </span>
  );
}

function IvrOptionsBlock({ ivr }: { ivr: Ivr }) {
  const menu = ivr.menu || [];
  const hasSpecial = Boolean(ivr.timeout || ivr.invalid);
  if (!menu.length && !hasSpecial) {
    return <span className="text-sm text-gray-400 italic">No key destinations configured</span>;
  }

  return (
    <div className="space-y-1.5 text-sm">
      {menu.map((opt) => (
        <div key={opt.key} className="flex items-start gap-2">
          <KeyChip label={opt.key} />
          <span className="text-gray-500 dark:text-gray-400 shrink-0">→</span>
          <span className="text-gray-800 dark:text-gray-100">{opt.label}</span>
        </div>
      ))}
      {ivr.timeout ? (
        <div className="flex items-start gap-2 pt-1 border-t border-gray-100 dark:border-gray-700/80">
          <span className="inline-flex items-center h-6 px-1.5 rounded-md bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 text-[10px] font-bold uppercase tracking-wide">
            Timeout
          </span>
          <span className="text-gray-500 dark:text-gray-400 shrink-0">→</span>
          <span className="text-gray-800 dark:text-gray-100">{ivr.timeout.label}</span>
        </div>
      ) : null}
      {ivr.invalid ? (
        <div className="flex items-start gap-2">
          <span className="inline-flex items-center h-6 px-1.5 rounded-md bg-rose-100 dark:bg-rose-900/40 text-rose-900 dark:text-rose-200 text-[10px] font-bold uppercase tracking-wide">
            Invalid
          </span>
          <span className="text-gray-500 dark:text-gray-400 shrink-0">→</span>
          <span className="text-gray-800 dark:text-gray-100">{ivr.invalid.label}</span>
        </div>
      ) : null}
    </div>
  );
}

export function IvrsTab({ customerId }: IvrsTabProps) {
  const { canManage } = useModuleAccess('ivrs');

  const ivrsList = useAction(backendApi.provisioning.ivrsList);
  const ivrsCreate = useAction(backendApi.provisioning.ivrsCreate);

  const [ivrs, setIvrs] = useState<Ivr[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    number: '',
    name: '',
    prompt: 'default',
    timeoutDestExtension: '',
    invalidDestExtension: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await ivrsList({ customerId, source: 'ui' });
      setIvrs(rows);
    } catch (error) {
      setIvrs([]);
      toast.error(error instanceof Error ? error.message : 'Failed to load IVRs');
    } finally {
      setLoading(false);
    }
  }, [customerId, ivrsList]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      const data = await ivrsCreate({
        customerId,
        source: 'ui',
        number: form.number,
        name: form.name,
        prompt: form.prompt,
        timeoutDest: form.timeoutDestExtension ? 'extension' : undefined,
        timeoutDestExtension: form.timeoutDestExtension || undefined,
        invalidDest: form.invalidDestExtension ? 'extension' : undefined,
        invalidDestExtension: form.invalidDestExtension || undefined,
      });
      toast.success(data.promptNote ? `IVR created — ${data.promptNote}` : 'IVR created');
      setShowForm(false);
      setForm({
        number: '',
        name: '',
        prompt: 'default',
        timeoutDestExtension: '',
        invalidDestExtension: '',
      });
      void load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Create failed');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">IVRs</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {ivrs.length} IVR menu{ivrs.length === 1 ? '' : 's'} · keypress and timeout destinations
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => setShowForm(!showForm)}>
              <Plus className="h-4 w-4 mr-1" />
              Create IVR
            </Button>
          )}
        </div>
      </div>

      {canManage && showForm && (
        <Card animate={false}>
          <form onSubmit={handleCreate} className="grid gap-4 md:grid-cols-2">
            <Input
              label="Number"
              value={form.number}
              onChange={(e) => setForm({ ...form, number: e.target.value })}
              required
            />
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              label="Prompt"
              value={form.prompt}
              onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              placeholder="default"
            />
            <Input
              label="Timeout → Extension"
              value={form.timeoutDestExtension}
              onChange={(e) => setForm({ ...form, timeoutDestExtension: e.target.value })}
              placeholder="100"
            />
            <Input
              label="Invalid → Extension"
              value={form.invalidDestExtension}
              onChange={(e) => setForm({ ...form, invalidDestExtension: e.target.value })}
              placeholder="100"
            />
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit">Create</Button>
              <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <Card animate={false}>
          <Loader text="Loading IVRs and menu destinations..." />
        </Card>
      ) : ivrs.length === 0 ? (
        <Card animate={false}>
          <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
            No IVRs configured.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {ivrs.map((ivr) => (
            <Card key={ivr.id} animate={false} className="overflow-hidden p-0">
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                <span className="inline-flex font-mono font-semibold tabular-nums bg-indigo-50 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200 px-2 py-0.5 rounded-md">
                  {ivr.number}
                </span>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white flex-1 min-w-0 truncate">
                  {ivr.name}
                </h4>
                {ivr.prompt ? (
                  <span
                    className="text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate"
                    title={ivr.prompt}
                  >
                    Prompt: {ivr.prompt}
                  </span>
                ) : null}
              </div>
              <div className="px-4 py-3">
                <IvrOptionsBlock ivr={ivr} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
